import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { AppDispatch, RootState } from '../../../store/store';
import { fetchCourseDetail, clearSelectedCourse } from '../store/coursesSlice';
import { EnrichedEnrollment, EnrollmentState, getMyEnrollments } from '../api/enrollments';
import type { PublishedOffer } from '../api/published-offer';
import { OCCUPYING_ENROLLMENT_STATES } from '../components/enrollmentRequest';
import { COURSE_LANGUAGE_OPTIONS } from '../domain/catalog';
import CourseMeetings from '../components/CourseMeetings';
import ReviewsSection from '../components/ReviewsSection';
import { getCourseReviews } from '../api/reviews';
import { formatMoney } from '../../../commodity/format/money';
import { catalogPath } from '../domain/catalog-context';

/**
 * States in which a child is, or was, on this course: the seat-holding states
 * the server uses for duplicate detection — including a request awaiting the
 * teacher (D-P07-03) — plus a completed cycle.
 */
const OCCUPYING_STATES: readonly EnrollmentState[] = [...OCCUPYING_ENROLLMENT_STATES, 'Completed'];

const BANNER_STATE: Partial<Record<EnrollmentState, string>> = {
  PendingPayment: 'pago pendiente',
  PendingTeacherAcceptance: 'esperando al profesor',
};

const CourseDetail: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedCourse, loading, error } = useSelector((state: RootState) => state.courses);
  const user = useSelector((state: RootState) => state.auth.user);
  const activeContext = useSelector((state: RootState) => state.context.activeContext);

  const [myEnrollments, setMyEnrollments] = useState<EnrichedEnrollment[]>([]);

  useEffect(() => {
    if (courseId) {
      dispatch(fetchCourseDetail(courseId));
    }
    return () => {
      dispatch(clearSelectedCourse());
    };
  }, [dispatch, courseId]);

  // A parent needs to know their child is already in this course BEFORE
  // committing to checkout. The server rejects the duplicate, but only at the
  // end of the flow — QA hit exactly that.
  useEffect(() => {
    if (user?.role !== 'PARENT') return;
    let cancelled = false;
    getMyEnrollments()
      .then(({ enrollments }) => {
        if (!cancelled) setMyEnrollments(enrollments ?? []);
      })
      .catch(() => {
        // Non-fatal: the page still works, the server remains the backstop.
      });
    return () => {
      cancelled = true;
    };
  }, [user?.role]);

  const onThisCourse = useMemo(
    () =>
      myEnrollments.filter((e) => e.courseId === courseId && OCCUPYING_STATES.includes(e.state)),
    [myEnrollments, courseId],
  );

  const activeChildEnrollment = activeContext
    ? onThisCourse.find((e) => e.childId === activeContext.studentId)
    : undefined;

  // PRT-08-FR-09 / PRT-BR-P02-09 (AC-14) — inside a learner's view the banner
  // speaks for that learner only. It listed every child of the account on the
  // course, so acting as one child showed the siblings' enrollments too. The
  // aggregate belongs to the parent view, where it is still shown.
  const bannerEnrollments = activeContext
    ? onThisCourse.filter((e) => e.childId === activeContext.studentId)
    : onThisCourse;

  if (loading) return <p>Cargando curso...</p>;
  if (error) return <p style={{ color: '#e03131' }}>{error}</p>;
  if (!selectedCourse) return <p>Curso no encontrado.</p>;

  const course = selectedCourse;
  const backToCatalog = catalogPath(searchParams);

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      {/* PRT-BR-P03-08 — back to the result set this course was chosen from.
          It went to a bare `/courses`, so narrowing the catalog to three
          courses and opening one of them threw the narrowing away: the filter
          context the handoff from P03 carries was arriving here and being
          dropped on the way back. */}
      <Link
        to={backToCatalog}
        style={{
          display: 'inline-block',
          color: '#0366d6',
          marginBottom: '16px',
          fontSize: '14px',
          textDecoration: 'none',
        }}
      >
        &larr; Volver a los resultados
      </Link>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <span
            style={{
              fontSize: '12px',
              backgroundColor: '#e7f5ff',
              color: '#1971c2',
              padding: '2px 8px',
              borderRadius: '4px',
            }}
          >
            {course.subject}
          </span>
          <h1 style={{ margin: '8px 0' }}>{course.title}</h1>
        </div>
      </div>

      <p style={{ color: '#555', lineHeight: 1.6, marginBottom: '24px' }}>{course.description}</p>

      {/* Course info cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '12px',
          marginBottom: '24px',
        }}
      >
        <InfoCard label="Precio" value={formatMoney(course.price, course.currency)} />
        <InfoCard label="Duracion" value={`${course.duration} min`} />
        <InfoCard label="Max. Estudiantes" value={String(course.maxStudents)} />
        {/* Only when the course says so. A course written before the field
            existed could have been taught in either language, and a card
            reading "Español" over a class taught in English is worse than no
            card at all. */}
        {course.language && (
          <InfoCard
            label="Idioma"
            value={
              COURSE_LANGUAGE_OPTIONS.find((o) => o.value === course.language)?.label ??
              course.language
            }
          />
        )}
      </div>

      {/* Grade levels */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ marginBottom: '8px' }}>Niveles</h3>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {course.gradeLevel.map((g) => (
            <span
              key={g}
              style={{
                backgroundColor: '#f1f3f5',
                padding: '4px 12px',
                borderRadius: '16px',
                fontSize: '13px',
              }}
            >
              {g}
            </span>
          ))}
        </div>
      </div>

      {/* ── Course content (ADR-007 S4.3, TCH-09-FR-05) ───────────────────
          Each section renders only when the teacher wrote it. A heading over
          an empty body reads as a section they failed to fill in, which is a
          worse thing to tell a family than saying nothing. */}
      <ContentSection title="Resumen" body={course.overview} />

      {course.learningObjectives && course.learningObjectives.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '8px' }}>Objetivos de aprendizaje</h3>
          <ul style={{ color: '#555', lineHeight: 1.6, margin: 0, paddingLeft: 20 }}>
            {course.learningObjectives.map((objective, i) => (
              <li key={i}>{objective}</li>
            ))}
          </ul>
        </div>
      )}

      <ContentSection title="Estructura de la clase" body={course.classStructure} />
      <ContentSection title="Qué esperar" body={course.whatToExpect} />

      <ReviewsSection
        subject="COURSE"
        subjectId={course.listingId}
        load={getCourseReviews}
        title="Reseñas del curso"
      />

      {/* Teacher card — the same approved projection the teacher's profile page
          reads (PRT-BR-P05-19). When there is none to show, or it could not be
          read, the card says so rather than vanishing or being filled in from
          the course (PRT-BR-P05-20, AC-18). */}
      <div
        style={{
          border: '1px solid #d1d5da',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
        }}
      >
        <h3 style={{ margin: '0 0 8px 0' }}>Profesor</h3>
        {course.teacher ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ margin: 0, fontWeight: 600 }}>
                {course.teacher.firstName} {course.teacher.lastName}
              </p>
              {course.teacher.verified && (
                <p style={{ margin: '2px 0 0', color: '#2b8a3e', fontSize: '13px' }}>
                  Profesor verificado
                </p>
              )}
              {course.teacher.bio && (
                <p style={{ margin: '4px 0 0', color: '#555', fontSize: '14px' }}>
                  {course.teacher.bio}
                </p>
              )}
              <button
                onClick={() => navigate(`/teachers/${course.teacher!.teacherId}`)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#0366d6',
                  cursor: 'pointer',
                  padding: 0,
                  marginTop: 8,
                  fontSize: 14,
                }}
              >
                Ver perfil completo
              </button>
            </div>
          </div>
        ) : (
          <p role="status" style={{ margin: 0, color: '#666', fontSize: '14px' }}>
            La información del profesor no está disponible en este momento.
          </p>
        )}
      </div>

      <div
        style={{
          border: '1px solid #d1d5da',
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 12,
          fontSize: 14,
          color: '#444',
        }}
      >
        <strong style={{ display: 'block', marginBottom: 4 }}>Formato</strong>
        {course.offerMode === 'ONE_ON_ONE' ? 'Clase individual' : 'Clase en grupo'}
        {course.durationMode === 'FIXED' && course.durationWeeks
          ? ` · ${course.durationWeeks} semanas`
          : ''}
      </div>

      <CourseMeetings course={course} />

      {bannerEnrollments.length > 0 && (
        <div
          style={{
            background: '#eaf2fb',
            border: '1px solid #4a90e2',
            borderRadius: 8,
            padding: '12px 14px',
            marginBottom: 12,
            fontSize: 14,
            color: '#1c4f80',
          }}
        >
          <strong style={{ display: 'block', marginBottom: 4 }}>
            {bannerEnrollments.length === 1
              ? 'Ya tienes un alumno en este curso'
              : 'Ya tienes alumnos en este curso'}
          </strong>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {bannerEnrollments.map((e) => (
              <li key={e.enrollmentId}>
                {e.childName ?? 'Tu alumno'} — {BANNER_STATE[e.state] ?? 'inscrito'}{' '}
                <Link to={`/enrollments/${e.enrollmentId}`}>Ver inscripción</Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {activeChildEnrollment ? (
        <button
          onClick={() => navigate(`/enrollments/${activeChildEnrollment.enrollmentId}`)}
          style={{
            width: '100%',
            padding: '14px',
            backgroundColor: '#fff',
            color: '#1971c2',
            border: '2px solid #1971c2',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Ver inscripción de {activeChildEnrollment.childName ?? 'tu alumno'}
        </button>
      ) : !course.offer ? (
        // DEC-S2-S04-01: no offer, nothing to bind a family to.
        <p role="status" style={{ textAlign: 'center', color: '#666', fontSize: 14 }}>
          Este curso no admite inscripciones por ahora.
        </p>
      ) : (
        <button
          // The offer travels with the handoff, so what is confirmed on the next
          // screen is what was read on this one.
          onClick={() =>
            navigate(
              `/courses/${course.listingId}/enroll?offer=${encodeURIComponent(
                (course.offer as PublishedOffer).offerId,
              )}`,
            )
          }
          style={{
            width: '100%',
            padding: '14px',
            backgroundColor: '#1971c2',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Inscribirse
        </button>
      )}
    </div>
  );
};

/**
 * One written section of the course page.
 *
 * Renders nothing at all when the field is absent, rather than a heading with
 * an empty body — the same distinction the shared EmptyState draws elsewhere:
 * a section that is not part of this course is not a section the teacher left
 * blank.
 */
const ContentSection: React.FC<{ title: string; body?: string }> = ({ title, body }) =>
  body ? (
    <div style={{ marginBottom: '24px' }}>
      <h3 style={{ marginBottom: '8px' }}>{title}</h3>
      <p style={{ color: '#555', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>{body}</p>
    </div>
  ) : null;

const InfoCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div
    style={{
      border: '1px solid #e9ecef',
      borderRadius: '8px',
      padding: '12px',
      textAlign: 'center',
    }}
  >
    <div style={{ fontSize: '12px', color: '#868e96', marginBottom: '4px' }}>{label}</div>
    <div style={{ fontSize: '20px', fontWeight: 700 }}>{value}</div>
  </div>
);

export default CourseDetail;
